import { SHELL, WHITE_LABEL } from '../lib/branding.ts';
import { Link } from '@tanstack/react-router';
import { Card, CardBody } from '../components/ui/card.tsx';
import { marketingConsent } from '../legal/content/marketing.ts';
import { privacyPolicy } from '../legal/content/privacy.ts';
import { serviceTerms } from '../legal/content/service.ts';
import { subscriptionTerms } from '../legal/content/subscription.ts';
import type { Block, LegalDoc } from '../legal/types.ts';

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'h2':
      return <h2 className="mt-6 font-semibold text-slate-900">{block.text}</h2>;
    case 'p':
      return <p className="mt-2 text-sm leading-6 text-slate-700">{block.text}</p>;
    case 'ol':
      return (
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm leading-6 text-slate-700">
          {block.items.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
      );
    case 'ul':
      return (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          {block.items.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-500">
                {block.head.map((h) => (
                  <th key={h} className="px-2 py-1.5">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row.join('|')} className="border-b border-slate-100 align-top">
                  {row.map((cell) => (
                    <td key={cell} className="px-2 py-1.5 text-slate-700">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/** `back` 이 false 면 돌아가기 링크 대신 창 닫기 — 구독 시작 화면이 새 창으로 연다 */
function DocPage({ doc, back = true }: { doc: LegalDoc; back?: boolean }) {
  return (
    <div className="mx-auto max-w-3xl p-4">
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 pb-3">
            <h1 className="text-xl font-bold">{doc.title}</h1>
            <span className="text-xs text-slate-500">시행일 {doc.effectiveDate}</span>
          </div>
          {doc.draft ? (
            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
              검토 전 초안입니다. 확정본으로 바뀌면 시행일과 판이 함께 바뀌고 재동의를 받습니다.
            </p>
          ) : null}
          {doc.blocks.map((block, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: 정적 문서라 순서가 곧 정체성이다
            <BlockView key={i} block={block} />
          ))}
          <div className="mt-6 border-t border-slate-100 pt-3 text-sm">
            {back ? (
              <Link to="/signup" className="text-slate-600 hover:underline">
                ← 가입 화면으로
              </Link>
            ) : (
              <button type="button" className="text-slate-600 hover:underline" onClick={() => window.close()}>
                창 닫기
              </button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * 화이트라벨 호스트의 약관 화면 — 우리(비하이브) 약관을 보이지 않는다. 판매 주체는 대행사이고 문서도 대행사 것이다
 * (261025-agency-white-label). 대행사가 준 주소가 있으면 그리로 안내한다
 */
function AgencyDocPage({ title, url }: { title: string; url: string | null }) {
  return (
    <div className="mx-auto max-w-3xl p-4">
      <Card>
        <CardBody>
          <h1 className="border-b border-slate-100 pb-3 text-xl font-bold">{title}</h1>
          <p className="mt-4 text-slate-700 text-sm">
            {SHELL.company.name}의 {title}을 확인해 주세요.{' '}
            {url ? (
              <a href={url} target="_blank" rel="noreferrer" className="font-semibold underline">
                문서 열기
              </a>
            ) : null}
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

export const TermsPage = () =>
  WHITE_LABEL ? <AgencyDocPage title="이용약관" url={SHELL.termsUrl} /> : <DocPage doc={serviceTerms} />;
export const PrivacyPage = () =>
  WHITE_LABEL ? <AgencyDocPage title="개인정보처리방침" url={SHELL.privacyUrl} /> : <DocPage doc={privacyPolicy} />;
export const MarketingConsentPage = () =>
  WHITE_LABEL ? <AgencyDocPage title="개인정보처리방침" url={SHELL.privacyUrl} /> : <DocPage doc={marketingConsent} />;
/** 구독 서비스 이용약관 전문 — 구독 시작 화면이 새 창으로 연다(서면 계약서를 갈음한다, 261011-subscription-billing-payup) */
// ★ 화이트라벨이어도 우리 구독 약관이다 — 구독을 결제하는 것은 대행사 직원이고 대행사가 우리 고객이다(대행사 고객은 결제 화면을 보지 않는다)
export const SubscriptionTermsPage = () => <DocPage doc={subscriptionTerms} back={false} />;
